import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
// @ts-ignore
import { CommonModule } from '@angular/common';
// @ts-ignore
import { Component, OnInit, signal, HostListener } from '@angular/core';
// @ts-ignore
import { FormsModule } from '@angular/forms';
// @ts-ignore
import { firstValueFrom } from 'rxjs';
import { RegrasComissaoService } from '../../services/regras-comissao.service';
import { GrupoProduto, Vendedor, CabecalhoRegra, RegraComissaoSalva } from '../../models/regras-comissao.model';
import { MoneyMaskDirective } from '../../directives/money-mask.directive';
import {SankhyaGatewayService} from "../../services/sankhya-gateway.service";

// Tipagem auxiliar para o Signal da Nova Regra
type TipoNovaRegra = {
  tipo: string;
  unidadeRef: string;
  valorRefInicial: number | string | null;
  valorRefFinal: number | string | null;
  unidadeComissao: string;
  valorComissao: number | string | null;
};

// Tipagem auxiliar para o Signal da Simulação
type TipoSimulacao = {
  indexRegra: string | number;
  codGrupo: string | number;
  valorVenda: string | number;
  descontoVenda: string | number;
  resultados: any[] | null;
};

// @ts-ignore
@Component({
  selector: 'app-gpl-regras-comissao',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule, MoneyMaskDirective],
  templateUrl: './gpl-regras-comissao.component.html',
  styleUrl: './gpl-regras-comissao.component.css'
})
export class GplRegrasComissaoComponent implements OnInit {

  // NAVEGAÇÃO ENTRE TELAS
  tela = signal<'lista' | 'formulario'>('lista');

  // LISTA DE REGRAS SALVAS (memória)
  listaDeRegras = signal<RegraComissaoSalva[]>([]);
  indiceEdicao = signal<number>(-1); // -1 = nova regra, >= 0 = editando

  // ESTADO DO FORMULÁRIO
  carregando = signal(false);
  loadingMsg = signal('');
  salvando = signal(false);

  regras = signal<any[]>([]); // Permite valorRefInicial e valorRefFinal
  cabecalho = signal<CabecalhoRegra>({ nome: '', dataInicio: '', dataFim: '', codRegra: '' });

  // Grupos de produtos
  grupos = signal<GrupoProduto[]>([]);
  carregandoGrupos = signal(false);
  dropGrupos = signal(false);
  filtroGrupo = '';
  todosGruposSelecionados = signal(true);

  // Vendedores
  vendedores = signal<Vendedor[]>([]);
  carregandoVendedores = signal(false);
  dropVendedores = signal(false);
  filtroVendedor = '';
  todosVendedoresSelecionados = signal(true);

  novaRegra = signal<TipoNovaRegra>({
    tipo: '',
    unidadeRef: 'R$',
    valorRefInicial: null,
    valorRefFinal: null,
    unidadeComissao: '%',
    valorComissao: null
  });

  // Action Menus
  actionMenuAberto = signal<number>(-1);
  actionMenuParamAberto = signal<number>(-1);

  // TOAST / ALERT
  alertaModal = signal(false);
  alertaTexto = signal('');
  alertaTipo = signal<'sucesso' | 'erro' | 'aviso'>('aviso');

  // CONFLITO MODAL (Save)
  modalConflitoAberto = signal(false);
  mensagemConflito = signal('');

  // LÓGICA DO SIMULADOR
  simuladorAberto = signal(false);
  simulacao = signal<TipoSimulacao>({
    indexRegra: '',
    codGrupo: '',
    valorVenda: '',
    descontoVenda: '',
    resultados: null
  });

  constructor(
      private readonly sankhyaGateway: SankhyaGatewayService
  ) {}

  @HostListener('document:click')
  onDocumentClick(): void {
    this.fecharDropdowns();
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnterPress(event: KeyboardEvent): void {
    const el = event.target as HTMLElement;

    if (el.tagName === 'TEXTAREA') return;
    if (el.tagName === 'BUTTON') return;

    // Multiselect Grupos
    if (el.classList.contains('multiselect-trigger') && !el.closest('.vendedor-wrapper')) {
      event.preventDefault();
      if (this.dropGrupos()) {
        this.dropGrupos.set(false);
        const btn = document.getElementById('btn-adicionar');
        if (btn) btn.focus();
      } else {
        this.dropGrupos.set(true);
        this.filtroGrupo = '';
      }
      return;
    }

    // Multiselect Vendedores
    if (el.classList.contains('multiselect-trigger') && el.closest('.vendedor-wrapper')) {
      event.preventDefault();
      if (this.dropVendedores()) {
        this.dropVendedores.set(false);
        this._avancarFoco(el);
      } else {
        this.dropVendedores.set(true);
        this.filtroVendedor = '';
      }
      return;
    }

    event.preventDefault();
    this._avancarFoco(el);
  }

  private _avancarFoco(el: HTMLElement): void {
    const focusableSelector = 'input:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusableNodes = document.querySelectorAll(focusableSelector);
    const focusable = Array.prototype.filter.call(focusableNodes, (element: HTMLElement) => {
      return element.offsetWidth > 0 || element.offsetHeight > 0 || element === document.activeElement;
    }) as HTMLElement[];

    const index = focusable.indexOf(el);
    if (index > -1 && index < focusable.length - 1) {
      focusable[index + 1].focus();
    }
  }

  ngOnInit(): void {
    this.carregarGrupos();
    this.carregarVendedores();
    this.carregarListaRegras();
  }

  // ============================
  // NAVEGAÇÃO E RESET
  // ============================
  async carregarListaRegras() {
    this.carregando.set(true);
    try {
      const resp: any = await firstValueFrom(this.sankhyaGateway.buscarListaRegras());
      const respBody = resp.responseBody;
      if (respBody && respBody.regras) {
        const regrasNormalizadas = respBody.regras.map((regra: any) => {
          if (regra.vendedores && !Array.isArray(regra.vendedores)) {
            regra.vendedores = [regra.vendedores]; // Força a virar uma lista [objeto]
          }
          return regra;
        });

        this.listaDeRegras.set(regrasNormalizadas);
      }
    } catch (e: any) {
      this.abrirAlerta('Erro ao carregar lista de regras: ' + (e.message || e), 'erro');
    } finally {
      this.carregando.set(false);
    }
  }

  irParaNovaRegra(): void {
    this.indiceEdicao.set(-1);
    this._resetarFormulario();
    this.tela.set('formulario');
  }

  irParaLista(): void {
    this.tela.set('lista');
  }

  private _resetarFormulario(): void {
    const anoAtual = new Date().getFullYear();
    const dInicio = `${anoAtual}-01-01`;
    const dFim = `${anoAtual}-12-31`;

    this.cabecalho.set({ nome: '', dataInicio: dInicio, dataFim: dFim, codRegra: '' });
    this.regras.set([]);
    this.novaRegra.set({ tipo: '', unidadeRef: 'R$', valorRefInicial: null, valorRefFinal: null, unidadeComissao: '%', valorComissao: null });

    this.grupos.update((gs: GrupoProduto[]) => gs.map((g: GrupoProduto) => ({ ...g, selecionado: true })));
    this.todosGruposSelecionados.set(true);
    this.dropGrupos.set(false);

    this.vendedores.update((vs: Vendedor[]) => vs.map((v: Vendedor) => ({ ...v, selecionado: false })));
    this.todosVendedoresSelecionados.set(false);
    this.dropVendedores.set(false);
  }

  async editarRegra(index: number): Promise<void> {
    const itemLista = this.listaDeRegras()[index];
    this.indiceEdicao.set(index);

    const codRegra = itemLista.cabecalho?.codRegra || (itemLista as any).codRegra;

    // Muda a tela e exibe loading
    this.tela.set('formulario');
    this.carregando.set(true);
    this.loadingMsg.set('Carregando detalhes da regra...');

    try {
      const resp: any = await firstValueFrom(this.sankhyaGateway.buscarDetalhesRegra({ codRegra }));
      const respBody = resp.responseBody;
      const detalhes = respBody.regra || respBody; // Ajuste conforme o retorno exato do seu backend

      if (detalhes.vendedores && !Array.isArray(detalhes.vendedores)) {
        detalhes.vendedores = [detalhes.vendedores];
      }

      // 1. Restaura cabeçalho
      this.cabecalho.set({
        codRegra: detalhes.cabecalho.codRegra,
        nome: detalhes.cabecalho.nome,
        dataInicio: this.extrairDataIso(detalhes.cabecalho.dataInicio),
        dataFim: this.extrairDataIso(detalhes.cabecalho.dataFim)
      });

      // 2. Restaura parâmetros
      this.regras.set(JSON.parse(JSON.stringify(detalhes.regras || [])));

      const codsSelecionados: Record<string, boolean> = {};
      (detalhes.regras || []).forEach((r: any) => {
        r.grupos.forEach((g: any) => { codsSelecionados[String(g.codGrupo)] = true; });
      });

      const isTodosGrupos = detalhes.regras && detalhes.regras.length > 0 && detalhes.regras[0].todosGrupos;

      // 3. Atualiza interface de grupos
      this.grupos.update((gs: GrupoProduto[]) => gs.map((g: GrupoProduto) => ({
        ...g,
        selecionado: isTodosGrupos ? true : !!codsSelecionados[String(g.codGrupo)]
      })));
      this.todosGruposSelecionados.set(this.grupos().every((g: GrupoProduto) => g.selecionado));

      // 4. Restaura vendedores
      const vendSelecionados: Record<string, boolean> = {};
      if (detalhes.vendedores) {
        detalhes.vendedores.forEach((v: Vendedor) => { vendSelecionados[String(v.codVend)] = true; });
      }
      this.vendedores.update((vs: Vendedor[]) => vs.map((v: Vendedor) => ({
        ...v,
        selecionado: !!vendSelecionados[String(v.codVend)]
      })));
      this.todosVendedoresSelecionados.set(this.vendedores().every((v: Vendedor) => v.selecionado));

    } catch (e: any) {
      this.abrirAlerta('Erro ao buscar detalhes da regra: ' + (e.message || e), 'erro');
      this.irParaLista();
    } finally {
      this.carregando.set(false);
      this.loadingMsg.set('');
    }
  }

  async excluirRegra(index: number): Promise<void> {
    const codRegra = this.listaDeRegras()[index].cabecalho.codRegra;
    const resp : any = await firstValueFrom(this.sankhyaGateway.excluirRegraComissao({ codRegra }));
    const respBody = resp.responseBody;
    this.carregarListaRegras();
    if (respBody.sucesso) {
      this.abrirAlerta(respBody.mensagem, 'sucesso');
    } else {
      this.abrirAlerta('Erro ao excluir regra: ' + (respBody.mensagem || 'Erro desconhecido'), 'erro');
    }
  }

  // ============================
  // GRUPOS E VENDEDORES
  // ============================
  async carregarGrupos() {
    this.carregandoGrupos.set(true);
    try {
      // Alterado para utilizar o sankhyaGateway com firstValueFrom
      const res: any = await firstValueFrom(this.sankhyaGateway.buscarGrupos());

      if (res && String(res.status).trim() === '1') {
        const body = res.responseBody as any;
        const list = (body?.grupos || []).map((g: any) => ({ ...g, selecionado: true }));
        this.grupos.set(list);
        this.todosGruposSelecionados.set(true);
      } else {
        let msg = res?.statusMessage || '';
        if (!msg && res?.responseBody) msg = (res.responseBody as any).erro || '';
        this.abrirAlerta('Erro ao carregar grupos: ' + msg, 'erro');
      }
    } catch (err: any) {
      this.abrirAlerta('Falha na comunicação (grupos): ' + (err.message || err), 'erro');
    } finally {
      this.carregandoGrupos.set(false);
    }
  }

  toggleDropGrupos(event?: Event): void {
    if (event) event.stopPropagation();
    this.dropGrupos.set(!this.dropGrupos());
    this.filtroGrupo = '';
    this.dropVendedores.set(false);
  }

  toggleGrupo(grupo: GrupoProduto, event?: Event): void {
    if (event) event.stopPropagation();
    grupo.selecionado = !grupo.selecionado;
    this.todosGruposSelecionados.set(this.grupos().every((g: GrupoProduto) => g.selecionado));
  }

  toggleTodosGrupos(event?: Event): void {
    if (event) event.stopPropagation();
    const novoValor = !this.todosGruposSelecionados();
    this.todosGruposSelecionados.set(novoValor);
    this.grupos.update((gs: GrupoProduto[]) => gs.map((g: GrupoProduto) => ({ ...g, selecionado: novoValor })));
  }

  labelGruposSelecionados(): string {
    if (this.carregandoGrupos()) return 'Carregando...';
    const selecionados = this.grupos().filter((g: GrupoProduto) => g.selecionado);
    if (selecionados.length === 0) return 'Nenhum grupo';
    if (selecionados.length === this.grupos().length && this.grupos().length > 0) return 'Todos os grupos';
    return selecionados.length + ' grupo(s)';
  }

  gruposFiltrados(): GrupoProduto[] {
    const term = this.filtroGrupo.trim().toLowerCase();
    if (!term) return this.grupos();
    return this.grupos().filter((g: GrupoProduto) => g.descricao.toLowerCase().includes(term));
  }

  async carregarVendedores() {
    this.carregandoVendedores.set(true);
    try {
      // Alterado para utilizar o sankhyaGateway com firstValueFrom
      const res: any = await firstValueFrom(this.sankhyaGateway.buscarVendedores());

      if (res && String(res.status).trim() === '1') {
        const body = res.responseBody as any;
        const list = (body?.vendedores || []).map((v: any) => ({ ...v, selecionado: false }));
        this.vendedores.set(list);
        this.todosVendedoresSelecionados.set(false);
      } else {
        let msg = res?.statusMessage || '';
        if (!msg && res?.responseBody) msg = (res.responseBody as any).erro || '';
        this.abrirAlerta('Erro ao carregar vendedores: ' + msg, 'erro');
      }
    } catch (err: any) {
      this.carregandoVendedores.set(false);
      this.abrirAlerta('Falha na comunicação (vendedores): ' + (err.message || err), 'erro');
    } finally {
      this.carregandoVendedores.set(false);
    }
  }

  toggleDropVendedores(event?: Event): void {
    if (event) event.stopPropagation();
    this.dropVendedores.set(!this.dropVendedores());
    this.filtroVendedor = '';
    this.dropGrupos.set(false);
  }

  toggleVendedor(vendedor: Vendedor, event?: Event): void {
    if (event) event.stopPropagation();
    vendedor.selecionado = !vendedor.selecionado;
    this.todosVendedoresSelecionados.set(this.vendedores().every((v: Vendedor) => v.selecionado));
  }

  toggleTodosVendedores(event?: Event): void {
    if (event) event.stopPropagation();
    const novoValor = !this.todosVendedoresSelecionados();
    this.todosVendedoresSelecionados.set(novoValor);
    this.vendedores.update((vs: Vendedor[]) => vs.map((v: Vendedor) => ({ ...v, selecionado: novoValor })));
  }

  labelVendedoresSelecionados(): string {
    if (this.carregandoVendedores()) return 'Carregando...';
    const selecionados = this.vendedores().filter((v: Vendedor) => v.selecionado);
    if (selecionados.length === 0) return 'Nenhum vendedor';
    if (selecionados.length === this.vendedores().length && this.vendedores().length > 0) return 'Todos os vendedores';
    return selecionados.length + ' vendedor(es)';
  }

  vendedoresFiltrados(): Vendedor[] {
    const term = this.filtroVendedor.trim().toLowerCase();
    if (!term) return this.vendedores();
    return this.vendedores().filter((v: Vendedor) => v.apelido.toLowerCase().includes(term));
  }

  tooltipVendedores(item: RegraComissaoSalva): string {
    if (!item || !item.vendedores) return '';
    return item.vendedores.map((v: Vendedor) => v.apelido).join(', ');
  }

  // ============================
  // AÇÕES MENUS E DROPDOWNS
  // ============================
  fecharDropdowns(): void {
    this.actionMenuAberto.set(-1);
    this.actionMenuParamAberto.set(-1);
    this.dropGrupos.set(false);
    this.dropVendedores.set(false);
  }

  toggleActionMenu(index: number, event?: Event): void {
    if (event) event.stopPropagation();
    this.actionMenuAberto.set(this.actionMenuAberto() === index ? -1 : index);
    this.actionMenuParamAberto.set(-1);
  }

  toggleActionMenuParam(index: number, event?: Event): void {
    if (event) event.stopPropagation();
    this.actionMenuParamAberto.set(this.actionMenuParamAberto() === index ? -1 : index);
    this.actionMenuAberto.set(-1);
  }

  // ============================
  // FORMULÁRIO DE PARÂMETROS
  // ============================
  aoMudarTipoParametro(): void {
    const tipo = this.novaRegra().tipo;
    const rule = { ...this.novaRegra() };

    if (tipo === 'FIXA') {
      rule.valorRefInicial = null;
      rule.valorRefFinal = null;
      rule.valorComissao = null;
      rule.unidadeRef = 'FIXA';
      rule.unidadeComissao = 'R$';
      this.grupos.update((gs: GrupoProduto[]) => gs.map((g: GrupoProduto) => ({ ...g, selecionado: g.descricao.toUpperCase().includes('MOTORES') })));
    } else if (tipo === 'PORCENTAGEM') {
      rule.valorRefInicial = null;
      rule.valorRefFinal = null;
      rule.valorComissao = null;
      rule.unidadeRef = '%';
      rule.unidadeComissao = '%';
      this.grupos.update((gs: GrupoProduto[]) => gs.map((g: GrupoProduto) => ({ ...g, selecionado: !g.descricao.toUpperCase().includes('MOTORES') })));
    } else if (tipo === 'VALOR') {
      rule.valorRefInicial = null;
      rule.valorRefFinal = null;
      rule.valorComissao = null;
      rule.unidadeRef = 'R$';
      rule.unidadeComissao = '%';
      this.grupos.update((gs: GrupoProduto[]) => gs.map((g: GrupoProduto) => ({ ...g, selecionado: !g.descricao.toUpperCase().includes('MOTORES') })));
    }

    this.novaRegra.set(rule);
    this.todosGruposSelecionados.set(this.grupos().every((g: GrupoProduto) => g.selecionado));
  }

  cabecalhoPreenchido(): boolean {
    const cab = this.cabecalho();
    return !!(cab.nome && cab.nome.trim().length > 0 && cab.dataInicio && cab.dataFim && cab.dataFim >= cab.dataInicio);
  }

  adicionarRegra(): void {
    const nr = this.novaRegra();

    if (!nr.tipo) {
      this.abrirAlerta('Selecione o tipo da regra', 'aviso');
      return;
    }

    if (nr.tipo === 'VALOR' && this.regras().some((r: any) => r.tipo === 'PORCENTAGEM')) {
      this.abrirAlerta('Você já adicionou um parâmetro de PORCENTAGEM. Não é possível misturar com VALOR.', 'aviso');
      return;
    }

    if (nr.tipo === 'PORCENTAGEM' && this.regras().some((r: any) => r.tipo === 'VALOR')) {
      this.abrirAlerta('Você já adicionou um parâmetro de VALOR. Não é possível misturar com PORCENTAGEM.', 'aviso');
      return;
    }

    if (nr.tipo !== 'FIXA' && (nr.valorRefInicial === null || nr.valorRefInicial === '')) {
      this.abrirAlerta('Informe o valor de referência', 'aviso');
      return;
    }

    const valIni = this.parseMoney(nr.valorRefInicial as string);
    const valFim = nr.valorRefFinal ? this.parseMoney(nr.valorRefFinal as string) : null;
    const valCom = this.parseMoney(nr.valorComissao as string);

    if (valFim !== null && valIni === valFim) {
      this.abrirAlerta('O valor de referência inicial não pode ser igual ao final', 'aviso');
      return;
    }

    if (valFim !== null && valIni > valFim) {
      this.abrirAlerta('O valor de referência inicial não pode ser menor que o final', 'aviso');
      return;
    }

    if (nr.valorComissao === null || nr.valorComissao === '' || valCom < 0) {
      this.abrirAlerta('Informe o valor da comissão', 'aviso');
      return;
    }

    const selecionados = this.grupos().filter((g: GrupoProduto) => g.selecionado);

    const novoInicio = valIni;
    const novoFim = valFim !== null ? valFim : Infinity;

    const conflito = this.regras().some((regraCadastrada: any) => {
      if (nr.tipo === 'FIXA') return false;

      const cadInicio = typeof regraCadastrada.valorRefInicial === 'number' ? regraCadastrada.valorRefInicial : this.parseMoney(regraCadastrada.valorRefInicial);
      let cadFim = regraCadastrada.valorRefFinal ? (typeof regraCadastrada.valorRefFinal === 'number' ? regraCadastrada.valorRefFinal : this.parseMoney(regraCadastrada.valorRefFinal)) : Infinity;

      const cruzouValores = (novoInicio <= cadFim) && (novoFim >= cadInicio);
      if (!cruzouValores) return false;

      const aplicaTodosCadastrado = regraCadastrada.todosGrupos;
      const aplicaTodosNovo = (selecionados.length === this.grupos().length);

      if (aplicaTodosCadastrado || aplicaTodosNovo) return true;

      return selecionados.some((gNovo: GrupoProduto) => {
        return regraCadastrada.grupos.some((gCadastrado: any) => gCadastrado.codGrupo === gNovo.codGrupo);
      });
    });

    if (conflito) {
      this.abrirAlerta('A faixa de valores conflita com uma regra já existente para os grupos selecionados!', 'aviso');
      return;
    }

    // Adiciona Regra
    this.regras.update((rs: any[]) => [...rs, {
      tipo: nr.tipo,
      unidadeRef: nr.unidadeRef,
      valorRefInicial: nr.tipo === 'FIXA' ? null : valIni,
      valorRefFinal: nr.tipo === 'FIXA' ? null : valFim,
      unidadeComissao: nr.unidadeComissao,
      valorComissao: valCom,
      todosGrupos: selecionados.length === this.grupos().length,
      grupos: selecionados.map((g: GrupoProduto) => ({ codGrupo: g.codGrupo, descricao: g.descricao }))
    }]);

    this._ordenarRegras();

    this.novaRegra.update((r: TipoNovaRegra) => ({ ...r, tipo: '', valorRefInicial: null, valorRefFinal: null, valorComissao: null }));
    this.abrirAlerta('Parâmetro adicionado!', 'sucesso');
  }

  private _ordenarRegras(): void {
    this.regras.update((rs: any[]) => {
      const arr = [...rs];
      arr.sort((a: any, b: any) => {
        const valA = this.parseMoney(a.valorRefInicial);
        const valB = this.parseMoney(b.valorRefInicial);

        const vazioA = a.valorRefInicial === null || a.valorRefInicial === '';
        const vazioB = b.valorRefInicial === null || b.valorRefInicial === '';

        if (vazioA && vazioB) return 0;
        if (vazioA) return 1;
        if (vazioB) return -1;
        return valA - valB;
      });
      return arr;
    });
  }

  removerRegra(index: number): void {
    this.regras.update((rs: any[]) => {
      const n = [...rs];
      n.splice(index, 1);
      return n;
    });
    this.abrirAlerta('Parâmetro removido', 'aviso');
  }

  editarParametro(index: number): void {
    const p = this.regras()[index];
    this.novaRegra.set({
      tipo: p.tipo,
      unidadeRef: p.unidadeRef,
      valorRefInicial: p.valorRefInicial,
      valorRefFinal: p.valorRefFinal,
      unidadeComissao: p.unidadeComissao,
      valorComissao: p.valorComissao
    });

    const codsSelecionados: Record<string, boolean> = {};
    if (p.grupos) {
      p.grupos.forEach((g: any) => { codsSelecionados[String(g.codGrupo)] = true; });
    }
    this.grupos.update((gs: GrupoProduto[]) => gs.map((g: GrupoProduto) => ({
      ...g,
      selecionado: p.todosGrupos ? true : !!codsSelecionados[String(g.codGrupo)]
    })));
    this.todosGruposSelecionados.set(this.grupos().every((g: GrupoProduto) => g.selecionado));

    this.removerRegra(index);
  }

  duplicarParametro(index: number): void {
    this.regras.update((rs: any[]) => {
      const copia = JSON.parse(JSON.stringify(rs[index]));
      return [...rs, copia];
    });
    this._ordenarRegras();
    this.abrirAlerta('Parâmetro duplicado', 'sucesso');
  }

  // ============================
  // SALVAR / MODAL CONFLITO
  // ============================
  fecharModalConflito(): void {
    this.modalConflitoAberto.set(false);
    this.mensagemConflito.set('');
  }

  async resolverConflito(acao: string) {
    this.fecharModalConflito();
    await this.salvarRegras(acao);
  }

  async salvarRegras(acaoConflito?: string) {
    if (!this.cabecalhoPreenchido()) {
      this.abrirAlerta('Preencha o cabeçalho antes de salvar', 'aviso');
      return;
    }
    if (this.regras().length === 0) {
      this.abrirAlerta('Adicione ao menos um parâmetro', 'aviso');
      return;
    }

    this.salvando.set(true);

    const item: any = {
      cabecalho: { ...this.cabecalho() },
      regras: JSON.parse(JSON.stringify(this.regras())),
      vendedores: this.vendedores().filter((v: Vendedor) => v.selecionado).map((v: Vendedor) => ({ codVend: v.codVend, apelido: v.apelido })),
      acaoConflito: acaoConflito || null
    };

    try {
      // Captura o retorno do back-end
      const resp: any = await firstValueFrom(this.sankhyaGateway.salvarRegraComissao(item));

      // 1. AVALIAÇÃO DE SOFT ERROR (O Java capturou o erro e mandou sucesso = false)
      if (resp && resp.sucesso === false) {
        const msgBackend = resp.mensagem || 'Erro desconhecido retornado pelo servidor.';

        // Verifica se a mensagem de erro é o aviso de conflito de vendedores
        if (msgBackend.includes('#CONFIRMAR#')) {
          const msgLimpa = msgBackend
              .replace('Excecao no servidor: ', '')
              .replace('Excecao no servidor:', '')
              .replace('#CONFIRMAR#', '')
              .trim();

          this.mensagemConflito.set(msgLimpa);
          this.modalConflitoAberto.set(true);
          return; // Para a execução aqui e espera o usuário clicar no Modal
        } else {
          // É uma mensagem de erro comum (ex: validação do Java)
          this.abrirAlerta(msgBackend, 'erro');
          return; // Para a execução aqui para não fechar a tela
        }
      }

      // 2. SUCESSO (sucesso é true)
      const msgSucesso = resp?.mensagem || 'Regra salva com sucesso!';
      this.abrirAlerta(msgSucesso, 'sucesso');

      // Atualiza a lista e volta pra tela inicial
      await this.carregarListaRegras();
      this.tela.set('lista');

    } catch (e: any) {
      // 3. HARD ERROR (Cai aqui se der erro 500, cair a internet, ou estourar erro não tratado no Java)
      const erroFatal = e.message || e;

      // Validação de fallback caso o gateway cuspa o erro de conflito como Exceção
      if (typeof erroFatal === 'string' && erroFatal.includes('#CONFIRMAR#')) {
        const msgLimpa = erroFatal.replace(/Excecao no servidor:?\s*/g, '').replace('#CONFIRMAR#', '').trim();
        this.mensagemConflito.set(msgLimpa);
        this.modalConflitoAberto.set(true);
      } else {
        this.abrirAlerta('Erro de comunicação: ' + erroFatal, 'erro');
      }
    } finally {
      this.salvando.set(false);
    }
  }

  limparRegras(): void {
    this.regras.set([]);
  }

  // ============================
  // FORMATADORES E HELPERS
  // ============================
  formatarData(data: string | Date): string {
    if (!data) return '-';
    if (data instanceof Date) {
      const dia = String(data.getDate()).padStart(2, '0');
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      const ano = data.getFullYear();
      return `${dia}/${mes}/${ano}`;
    }
    if (typeof data === 'string') {
      const p = data.split('-');
      if (p.length === 3) {
        return `${p[2].substring(0, 2)}/${p[1]}/${p[0]}`;
      }
    }
    return data;
  }

  labelTipo(tipo: string): string {
    const map: Record<string, string> = { VALOR: 'Valor', PORCENTAGEM: 'Porcentagem', FIXA: 'Fixa de' };
    return map[tipo] || tipo;
  }

  badgeClass(tipo: string): string {
    const map: Record<string, string> = { VALOR: 'badge-acima', PORCENTAGEM: 'badge-desconto', FIXA: 'badge-fixa' };
    return map[tipo] || '';
  }

  tooltipParamGrupos(regra: any): string {
    if (!regra || !regra.grupos) return '';
    return regra.grupos.map((g: any) => g.descricao).join(', ');
  }

  descrRegra(regra: any): string {
    const label = this.labelTipo(regra.tipo);
    let desc = '';

    if (regra.tipo === 'FIXA') {
      desc = '';
    } else {
      if (regra.valorRefFinal) {
        desc = ` de ${regra.unidadeRef} ${Number(regra.valorRefInicial).toFixed(2).replace('.', ',')} até ${regra.unidadeRef} ${Number(regra.valorRefFinal).toFixed(2).replace('.', ',')}`;
      } else {
        desc = ` a partir de ${regra.unidadeRef} ${Number(regra.valorRefInicial).toFixed(2).replace('.', ',')}`;
      }
    }

    return `${label}${desc} → Comissão de ${regra.unidadeComissao} ${Number(regra.valorComissao).toFixed(2).replace('.', ',')}`;
  }

  abrirAlerta(texto: string, tipo: 'sucesso' | 'erro' | 'aviso' = 'aviso'): void {
    this.alertaTexto.set(texto);
    this.alertaTipo.set(tipo);
    this.alertaModal.set(true);
  }

  fecharAlerta(): void {
    this.alertaModal.set(false);
  }

  formatarDinheiro(valor: number | string | null | undefined): string {
    if (valor == null || valor === '') return '-';
    return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // ============================
  // SIMULADOR (VIA BACKEND)
  // ============================
  abrirSimulador(): void {
    this.simulacao.set({ indexRegra: '', codGrupo: '', valorVenda: '', descontoVenda: '', resultados: null });
    this.simuladorAberto.set(true);
  }

  fecharSimulador(): void {
    this.simuladorAberto.set(false);
  }

  aoSelecionarRegraSimulacao(): void {
    this.simulacao.update((s: TipoSimulacao) => ({ ...s, resultados: null, codGrupo: '', valorVenda: '', descontoVenda: '' }));
  }

  private parseMoney(val: string | number): number {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    const str = val.replace(/\./g, '').replace(',', '.');
    return parseFloat(str) || 0;
  }

  private extrairDataIso = (dataString: any) => {
    if (!dataString) return '';
    // Se vier "2026-01-01T03:00:00Z", separa no 'T' e pega a primeira parte: "2026-01-01"
    return String(dataString).split('T')[0];
  };

  async simularVenda() {
    const sim = this.simulacao();
    if (sim.indexRegra === '' || sim.indexRegra === undefined) return;

    const regraSelecionada = this.listaDeRegras()[Number(sim.indexRegra)];
    const codRegra = regraSelecionada.cabecalho?.codRegra;

    if (!codRegra) {
      this.abrirAlerta('Não foi possível identificar o código desta regra.', 'erro');
      return;
    }

    const vVenda = this.parseMoney(sim.valorVenda);
    const vDesc = this.parseMoney(sim.descontoVenda);
    const gCod = parseInt(sim.codGrupo as string) || 0;

    if (gCod === 0) {
      this.abrirAlerta('Selecione um grupo de produtos para simular', 'aviso');
      return;
    }

    this.carregando.set(true);

    try {
      const payload = {
        codRegra: codRegra,
        valorVenda: vVenda,
        descontoVenda: vDesc,
        codGrupo: gCod
      };

      // Substituindo o Mock pela chamada real ao serviço do Sankhya
      const resp: any = await firstValueFrom(this.sankhyaGateway.simularComissao(payload));
      const respBody = resp.responseBody;

      if (respBody && respBody.sucesso) {
        this.simulacao.update((s: TipoSimulacao) => ({
          ...s,
          resultados: [{
            nomeRegra: regraSelecionada.cabecalho.nome,
            param: { tipo: 'Motor de Cálculo Oficial' },
            valorCalculado: respBody.valorFormatado, // Valor processado e devolvido pelo Java
            detalhe: 'Processado com sucesso no servidor'
          }]
        }));
      } else {
        // Tratamento de erro caso o servidor devolva sucesso = false (Soft Error)
        const msg = respBody?.mensagem || respBody?.erro || 'Não foi possível calcular a simulação.';
        this.abrirAlerta(msg, 'erro');
      }

    } catch (e: any) {
      // Tratamento usando alertaModal (Hard Error / Exceções)
      this.abrirAlerta('Erro ao simular: ' + (e.message || e), 'erro');
    } finally {
      this.carregando.set(false);
    }
  }
}